import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { userAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../../utils/helpers';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Sprout, TrendingUp, Plus, ArrowRight, ArrowLeft, Clock, Coins, Sparkles, X, Info } from 'lucide-react';

const STATUS_STYLES = {
  active: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  matured: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  closed: 'bg-muted text-muted-foreground border-border',
  frozen: 'bg-red-500/15 text-red-600 border-red-500/30',
};

const timeLeft = (isoStr) => {
  if (!isoStr) return null;
  const end = new Date(isoStr).getTime();
  const now = Date.now();
  const diff = end - now;
  if (diff <= 0) return 'auto-approving now';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${m}m`;
};

export const SavingsPage = () => {
  const [plans, setPlans] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPlanId, setOpenPlanId] = useState(null);
  const [openAmount, setOpenAmount] = useState('');
  const [openBusy, setOpenBusy] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([userAPI.listSavingsPlans(), userAPI.listSavingsAccounts()]);
      setPlans(p.data);
      setAccounts(a.data);
    } catch (e) {
      toast.error('Failed to load savings');
    } finally { setLoading(false); }
  };

  const openDetail = async (id) => {
    try {
      const res = await userAPI.getSavingsAccount(id);
      setDetail(res.data);
      setSelected(res.data.account);
    } catch (e) {
      toast.error('Failed to load account');
    }
  };

  const doOpen = async () => {
    const amt = parseFloat(openAmount);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    setOpenBusy(true);
    try {
      const res = await userAPI.openSavingsAccount({ plan_id: openPlanId, amount: amt });
      toast.success('Savings account opened');
      setOpenPlanId(null);
      setOpenAmount('');
      await loadAll();
      openDetail(res.data.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || 'Failed to open');
    } finally { setOpenBusy(false); }
  };

  const doDeposit = async () => {
    const amt = parseFloat(depositAmount);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    setBusy(true);
    try {
      await userAPI.depositSavings(selected.id, amt);
      toast.success('Deposit added to savings');
      setShowDeposit(false);
      setDepositAmount('');
      openDetail(selected.id);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || 'Failed');
    } finally { setBusy(false); }
  };

  const doWithdraw = async () => {
    const amt = parseFloat(withdrawAmount);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    setBusy(true);
    try {
      await userAPI.withdrawSavings(selected.id, amt);
      toast.success('Withdrawal requested — cooling-off started');
      setShowWithdraw(false);
      setWithdrawAmount('');
      openDetail(selected.id);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || 'Failed');
    } finally { setBusy(false); }
  };

  const cancelWithdrawal = async (wid) => {
    if (!window.confirm('Cancel this withdrawal request?')) return;
    try {
      await userAPI.cancelSavingsWithdrawal(wid);
      toast.success('Withdrawal cancelled');
      openDetail(selected.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || 'Failed');
    }
  };

  // === DETAIL VIEW ===
  if (selected && detail) {
    const acct = detail.account;
    const plan = detail.plan;
    const w = detail.active_withdrawal;
    const growthPct = acct.growth_percentage || 0;
    return (
      <FocusedLayout
        title="Savings"
        description="Grow your wealth with secure savings plans"
      >
        <div className="space-y-6" data-testid="savings-detail-page">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => { setSelected(null); setDetail(null); }} data-testid="savings-back">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to savings
            </Button>
            <Badge variant="outline" className={STATUS_STYLES[acct.status]}>{acct.status}</Badge>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <Card data-testid="savings-balance-card">
              <CardContent className="pt-5">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Savings Balance</p>
                <p className="text-3xl font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(acct.balance, acct.currency, 4)}</p>
                <p className="text-xs text-muted-foreground mt-1">Plan: {acct.plan_name}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Growth Earned</p>
                <p className="text-3xl font-semibold mt-1 text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>+{acct.total_growth_earned.toFixed(4)}</p>
                <p className="text-xs text-muted-foreground mt-1">{growthPct.toFixed(2)}% growth · {acct.days_saved} days saved</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Next Growth</p>
                <p className="text-3xl font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>+{(detail.next_growth_amount || 0).toFixed(4)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {detail.next_growth_at ? `at ${formatDate(detail.next_growth_at)}` : 'pending'}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Estimated 12-month Balance</p>
                <p className="text-3xl font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{(detail.estimated_balance_12m || 0).toFixed(2)}</p>
                <p className="text-xs text-muted-foreground mt-1">at {plan ? (plan.annual_growth_rate * 100).toFixed(2) : '0'}% APY</p>
              </CardContent>
            </Card>
          </div>

          {w && (
            <Card className="border-amber-500/40" data-testid="active-withdrawal">
              <CardContent className="pt-5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-medium flex items-center gap-2 text-amber-700 dark:text-amber-400">
                      <Clock className="h-4 w-4" /> Withdrawal in cooling-off
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Gross <strong>{w.gross_amount}</strong> {w.currency} · Fee {w.fee.toFixed(2)} · Penalty {w.penalty.toFixed(2)} · Net <strong>{w.net_amount.toFixed(2)}</strong> to main wallet
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Auto-approves in <strong>{timeLeft(w.cooling_off_ends_at)}</strong> unless admin intervenes.
                    </p>
                  </div>
                  {w.status === 'pending_cooling' && (
                    <Button variant="outline" size="sm" onClick={() => cancelWithdrawal(w.id)} data-testid="cancel-withdrawal-btn">
                      <X className="h-4 w-4 mr-2" /> Cancel request
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex gap-3 flex-wrap">
            <Button onClick={() => setShowDeposit(true)} disabled={acct.status !== 'active'} data-testid="deposit-savings-btn">
              <ArrowRight className="h-4 w-4 mr-2" /> Deposit to Savings
            </Button>
            <Button variant="outline" onClick={() => setShowWithdraw(true)} disabled={acct.status === 'closed' || acct.status === 'frozen' || !!w} data-testid="withdraw-savings-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Withdraw to Main Wallet
            </Button>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Savings History</CardTitle>
            </CardHeader>
            <CardContent>
              {detail.transactions.length === 0 ? (
                <p className="text-center py-6 text-muted-foreground">No activity yet.</p>
              ) : (
                <div className="divide-y divide-border">
                  {detail.transactions.map(t => (
                    <div key={t.id} className="py-3 flex items-center justify-between" data-testid={`savings-txn-${t.id}`}>
                      <div>
                        <p className="text-sm font-medium capitalize">{t.type.replace('_', ' ')}</p>
                        <p className="text-xs text-muted-foreground">{t.description} · {formatDate(t.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${t.amount > 0 ? 'text-emerald-600' : t.amount < 0 ? 'text-red-600' : ''}`}>
                          {t.amount > 0 ? '+' : ''}{t.amount.toFixed(4)} {t.currency}
                        </p>
                        <p className="text-xs text-muted-foreground">bal: {t.balance_after.toFixed(4)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Deposit dialog */}
        <Dialog open={showDeposit} onOpenChange={setShowDeposit}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Deposit to Savings</DialogTitle>
              <DialogDescription>Transfer {acct.currency} from your main wallet into this Smart Growth Savings account.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label>Amount ({acct.currency})</Label>
              <Input type="number" step="0.01" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} data-testid="deposit-amount-input" />
              {plan && (
                <p className="text-xs text-muted-foreground">
                  Min: {plan.min_deposit} · Max: {plan.max_deposit || 'no limit'} {acct.currency}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeposit(false)}>Cancel</Button>
              <Button onClick={doDeposit} disabled={busy} data-testid="confirm-deposit-btn">{busy ? 'Adding...' : 'Add to Savings'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Withdraw dialog */}
        <Dialog open={showWithdraw} onOpenChange={setShowWithdraw}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Withdraw to Main Wallet</DialogTitle>
              <DialogDescription>
                Your request will enter a {plan?.cooling_off_hours || 24}-hour cooling-off. It will auto-approve unless an administrator intervenes.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label>Amount ({acct.currency})</Label>
              <Input type="number" step="0.01" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} data-testid="withdraw-amount-input" />
              <p className="text-xs text-muted-foreground">Available: {acct.balance.toFixed(4)} {acct.currency}</p>
              {plan && (plan.withdrawal_fee_pct > 0 || plan.early_withdrawal_penalty_pct > 0) && (
                <div className="rounded-md bg-muted p-3 text-xs space-y-1">
                  {plan.withdrawal_fee_pct > 0 && <p>Withdrawal fee: {(plan.withdrawal_fee_pct * 100).toFixed(2)}%</p>}
                  {plan.early_withdrawal_penalty_pct > 0 && plan.lock_days > 0 && <p>Early withdrawal penalty: {(plan.early_withdrawal_penalty_pct * 100).toFixed(2)}% (before maturity)</p>}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowWithdraw(false)}>Cancel</Button>
              <Button onClick={doWithdraw} disabled={busy} data-testid="confirm-withdraw-btn">{busy ? 'Requesting...' : 'Request Withdrawal'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </FocusedLayout>
    );
  }

  // === LIST VIEW ===
  return (
    <FocusedLayout
      title="Savings"
      description="Grow your wealth with secure savings plans"
    >
      <div className="space-y-6" data-testid="savings-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl font-semibold flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Sprout className="h-8 w-8 text-emerald-600" />
              Smart Growth Savings
            </h1>
            <p className="mt-2 text-muted-foreground max-w-2xl">
              Move funds from your main wallet to a Smart Growth Savings wallet and earn daily rewards. Funds never leave the platform.
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-center py-8 text-muted-foreground">Loading...</p>
        ) : (
          <>
            {accounts.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg"><TrendingUp className="h-4 w-4" /> Your Savings Accounts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 md:grid-cols-2">
                    {accounts.map(a => (
                      <button
                        key={a.id}
                        onClick={() => openDetail(a.id)}
                        className="p-4 rounded-lg border border-border hover:bg-muted/60 text-left transition-colors"
                        data-testid={`savings-account-${a.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium">{a.plan_name}</p>
                          <Badge variant="outline" className={STATUS_STYLES[a.status]}>{a.status}</Badge>
                        </div>
                        <p className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{a.balance.toFixed(4)} {a.currency}</p>
                        <p className="text-xs text-emerald-600 mt-1">
                          +{a.total_growth_earned.toFixed(4)} earned · {a.growth_percentage.toFixed(2)}% growth
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">Since {formatDate(a.started_at)}</p>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg"><Sparkles className="h-4 w-4" /> Available Plans</CardTitle>
              </CardHeader>
              <CardContent>
                {plans.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Info className="h-8 w-8 mx-auto opacity-40 mb-2" />
                    <p>No plans available right now. Check back soon.</p>
                  </div>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    {plans.map(p => (
                      <div key={p.id} className="p-4 rounded-lg border border-border" data-testid={`plan-${p.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-semibold">{p.name}</p>
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                            {(p.annual_growth_rate * 100).toFixed(2)}% APY
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">{p.description || `${p.growth_type} growth · daily`}</p>
                        <div className="text-xs text-muted-foreground space-y-1">
                          <p>Currency: <span className="font-medium text-foreground">{p.currency}</span></p>
                          <p>Min deposit: {p.min_deposit || 0} · Max: {p.max_deposit || 'no limit'}</p>
                          {p.lock_days > 0 && <p>Lock: {p.lock_days} days</p>}
                          {p.withdrawal_fee_pct > 0 && <p>Withdrawal fee: {(p.withdrawal_fee_pct * 100).toFixed(2)}%</p>}
                          <p>Cooling-off: {p.cooling_off_hours}h</p>
                        </div>
                        <Button className="w-full mt-3" size="sm" onClick={() => { setOpenPlanId(p.id); setOpenAmount(''); }} data-testid={`open-plan-${p.id}`}>
                          <Plus className="h-4 w-4 mr-2" /> Open Savings Account
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Open plan dialog */}
      <Dialog open={!!openPlanId} onOpenChange={(v) => !v && setOpenPlanId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Start Saving</DialogTitle>
            <DialogDescription>Your first deposit will fund this savings account. You can add more later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Initial Deposit</Label>
            <Input type="number" step="0.01" value={openAmount} onChange={(e) => setOpenAmount(e.target.value)} placeholder="0.00" data-testid="open-amount-input" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenPlanId(null)}>Cancel</Button>
            <Button onClick={doOpen} disabled={openBusy} data-testid="confirm-open-btn">
              {openBusy ? 'Opening...' : 'Open Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FocusedLayout>
  );
};
